import { useMemo, useRef, useState } from 'react';

import { itemUnitsKey, useCustomers, useItems } from '@/api/masters';
import { cashKey } from '@/api/cash';
import { useApiMutation } from '@/api/mutations';
import { outstandingKey } from '@/api/outstanding';
import {
  createSale,
  findSellableGarment,
  saleInvoicesKey,
  useSaleAccounts,
  useSaleCounter,
  useSaleGarments,
  useSaleItemSuggestions,
  useSalePreview,
  type SaleInvoice,
  type SaleItemSuggestion,
  type SellableGarment,
} from '@/api/saleInvoices';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { DocumentHeading, DocumentLines, type DocumentColumn } from '@/components/DocumentSheet';
import {
  enterKeyScope,
  ENTER_OWNER_ATTRIBUTE,
  useAutoFocusFirstField,
} from '@/components/enterKeyNavigation';
import { Field, controlClass, textAreaClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { Button, Card, Notice, SectionTitle } from '@/components/PageHeading';
import { SearchPicker, type PickerOption } from '@/components/SearchPicker';
import { SplitBoxes } from '@/components/SplitBoxes';
import { useDebounced } from '@/components/useDebounced';
import { useSplitParts } from '@/components/useSplitParts';
import { ApiError } from '@core/api/problem';
import { formatAmount } from '@core/format/money';
import { fillOne } from '@core/cash/split';
import {
  cartFromParts,
  cartProblems,
  garmentCodeCandidates,
  garmentLine,
  garmentNote,
  lineDiscountInput,
  otherLine,
  paidSoFar,
  paymentProblems,
  saleRequest,
  sellWords,
  type CartLine,
} from '@core/sales/sale';
import type { Session } from '@core/session/types';
import { designShortNames } from '@core/units/codePick';
import { readGarmentCode } from '@core/units/garmentCode';
import { withReference } from '@core/units/referenceNumber';

const rupees = (value: number) => `₹${formatAmount(value)}`;

let nextKey = 0;
const freshKey = () => `k${++nextKey}`;

/** The first candidate the branch has a garment under; the last refusal when none answers. */
async function findFirstGarment(candidates: readonly string[]) {
  let last: unknown = null;

  for (const serial of candidates) {
    try {
      return await findSellableGarment(serial);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) {
        throw error;
      }

      last = error;
    }
  }

  throw last;
}

/** One row of the search's dropdown — a garment, an item sold before, or a new item by name. */
type Choice =
  | { kind: 'garment'; garment: SellableGarment }
  | { kind: 'item'; item: SaleItemSuggestion }
  | { kind: 'new'; name: string };

const choiceKey = (choice: Choice) =>
  choice.kind === 'garment'
    ? `g${choice.garment.itemUnitId}`
    : choice.kind === 'item'
      ? `i${choice.item.description}`
      : 'new';

/**
 * Shop › Sales › New sale (canvas "Sales flow", 2026-10-25) — what is being sold on the left, line
 * by line; the customer, the bill and how they are paying on the right.
 *
 * <p>
 * One search finds everything: the design's short name and then only the digits on its tag (the
 * phone's way, `units/codePick.ts`), a scan or a whole code, or a name — garments with their photo,
 * price and whatever holds them, then items sold before. Every figure on a line is the server's
 * preview; this screen only holds what was typed.
 * </p>
 */
export function SaleCounter({
  session,
  onClose,
  onSaved,
}: {
  session: Session;
  onClose: () => void;
  onSaved: (sale: SaleInvoice) => void;
}) {
  const branchId = session.branch?.id;
  const [lines, setLines] = useState<CartLine[]>([]);
  // A box per account (canvas "Money in, by account"): what goes into each is one payment naming it,
  // saved with the sale in one go.
  const accounts = useSaleAccounts(branchId);
  const { parts, change: setParts } = useSplitParts(accounts.data);
  const payments = useMemo(() => cartFromParts(parts), [parts]);
  const [customer, setCustomer] = useState<PickerOption | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [billDiscount, setBillDiscount] = useState('');
  const [notes, setNotes] = useState('');

  const [design, setDesign] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [scanError, setScanError] = useState<unknown>(null);
  const [scanning, setScanning] = useState(false);
  const searchBox = useRef<HTMLInputElement | null>(null);

  const allowance = useSaleCounter(branchId);
  const isBillOfSupply = allowance.data?.isBillOfSupply ?? false;
  const mayDiscount = allowance.data?.mayDiscount ?? false;
  const customers = useCustomers(branchId, { search: customerSearch, pageSize: 10 });
  const designs = designShortNames(useItems(branchId, { pageSize: 200 }).data?.items ?? []);
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  // What the dropdown asks: the chosen design and its digits, or a name of two letters or more.
  const settledText = useDebounced(typed, 200);
  const query =
    design !== null
      ? { designCode: design, digits: settledText }
      : typed.trim() !== '' && settledText.trim().length >= 2
        ? { search: settledText }
        : null;
  const garments = useSaleGarments(branchId, query);
  const suggestions = useSaleItemSuggestions(
    branchId,
    design === null && typed.trim() !== '' && settledText.trim().length >= 2 ? settledText : null,
  );
  const settled = settledText === typed && !garments.isFetching;

  const choices: Choice[] =
    query === null
      ? []
      : [
          ...(garments.data ?? []).map((garment) => ({ kind: 'garment' as const, garment })),
          ...(design === null
            ? [
                ...(suggestions.data ?? []).map((item) => ({ kind: 'item' as const, item })),
                ...(typed.trim() ? [{ kind: 'new' as const, name: typed.trim() }] : []),
              ]
            : []),
        ];
  const pickable = (choice: Choice) =>
    choice.kind !== 'garment' || choice.garment.refusal === 'None';
  const showing = open && (choices.length > 0 || design !== null);

  const customerId = customer === null ? null : Number(customer.id);
  const problems = cartProblems(lines);

  const request = useMemo(
    () =>
      saleRequest({
        customerId,
        lines,
        discount: lineDiscountInput(billDiscount),
        payments,
        notes,
      }),
    [customerId, lines, billDiscount, payments, notes],
  );

  // Previewed only once it could be sent: the server's arithmetic, never ours.
  const preview = useSalePreview(problems.length === 0 ? { ...request, payments: [] } : null);
  const figures = problems.length === 0 && !preview.isError ? preview.data : undefined;
  const total = figures?.invoiceTotal ?? null;
  const paid = paidSoFar(payments);
  const paying = paymentProblems(total, payments, customerId);
  const ready =
    problems.length === 0 && total !== null && !preview.isFetching && paying.length === 0;
  const left = total === null ? null : Math.max(0, total - paid);

  const save = useApiMutation<SaleInvoice, void>({
    mutationFn: () => createSale(request),
    success: (saved) => `${saved.documentNumber} — ${rupees(saved.invoiceTotal)}.`,
    invalidates: [saleInvoicesKey, cashKey, outstandingKey, itemUnitsKey],
    onSuccess: onSaved,
  });
  const busy = save.isPending;

  const resetSearch = () => {
    setTyped('');
    setDesign(null);
    setActive(0);
    setOpen(false);
    searchBox.current?.focus();
  };

  const addGarment = (garment: SellableGarment) => {
    const refusal = garmentNote(garment);

    if (lines.some((line) => line.itemUnitId === garment.itemUnitId)) {
      setNote(`${garment.serialCode} is already on this sale.`);
    } else if (refusal) {
      setNote(`${garment.serialCode}: ${refusal}`);
    } else {
      setLines((was) => [...was, garmentLine(garment, freshKey())]);
      setNote(null);
      resetSearch();
    }
  };

  const pick = (choice: Choice) => {
    setScanError(null);

    if (choice.kind === 'garment') {
      addGarment(choice.garment);
    } else {
      const line =
        choice.kind === 'item'
          ? otherLine(freshKey(), choice.item)
          : { ...otherLine(freshKey()), description: choice.name };
      setLines((was) => [...was, line]);
      setNote(null);
      resetSearch();
    }
  };

  // A scanner types the whole code and Enter faster than the dropdown can answer: read it as a tag.
  const lookUp = async () => {
    const text = typed.trim();

    if (!text) {
      return;
    }

    setScanError(null);
    setScanning(true);

    try {
      addGarment(await findFirstGarment(garmentCodeCandidates(text, readGarmentCode(text).exact)));
    } catch (error) {
      setScanError(error);
    } finally {
      setScanning(false);
    }
  };

  const onSearchKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      // A closed list only opens; the highlight moves once it can be seen.
      if (!showing) {
        setOpen(true);
        return;
      }

      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((was) => Math.min(Math.max(was + step, 0), Math.max(choices.length - 1, 0)));
    } else if (event.key === 'Escape') {
      if (showing) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    } else if (event.key === 'Backspace' && typed === '' && design !== null) {
      setDesign(null);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const choice = choices[active];

      if (settled && choice && pickable(choice)) {
        pick(choice);
      } else if (design === null) {
        void lookUp();
      }
    }
  };

  const setLine = (key: string, change: Partial<CartLine>) =>
    setLines((was) => was.map((line) => (line.key === key ? { ...line, ...change } : line)));

  const lineFigure = (line: CartLine) => figures?.lines[lines.indexOf(line)];
  const numberLabel = (line: CartLine, what: string) => `Line ${lines.indexOf(line) + 1} ${what}`;

  const columns: DocumentColumn<CartLine>[] = [
    {
      key: 'item',
      title: 'Item',
      render: (line) =>
        line.kind === 'Garment' ? (
          <div className="flex items-center gap-3">
            <Thumb url={line.photoUrl} />
            <div>
              <p className="mb-0 font-semibold text-gray-900">{line.description}</p>
              <p className="text-default font-mono-nums mb-0 text-[length:var(--scale-list-meta)]">
                {withReference(line.serialCode ?? '', line.referenceNumber)} ·{' '}
                {line.catalogueHsn ? `HSN ${line.catalogueHsn}` : 'no HSN on its design'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex min-w-48 flex-col gap-1">
            <input
              aria-label={numberLabel(line, 'item')}
              placeholder="What is it — a stole, a mojri"
              value={line.description}
              disabled={busy}
              onChange={(event) => setLine(line.key, { description: event.target.value })}
              className={controlClass}
            />
            <input
              aria-label={numberLabel(line, 'HSN')}
              placeholder="HSN"
              value={line.hsnCode}
              disabled={busy}
              onChange={(event) => setLine(line.key, { hsnCode: event.target.value })}
              className={`${controlClass} font-mono-nums max-w-28`}
            />
          </div>
        ),
    },
    {
      key: 'qty',
      title: 'Qty',
      numeric: true,
      render: (line) =>
        line.kind === 'Garment' ? (
          '1'
        ) : (
          <input
            type="number"
            aria-label={numberLabel(line, 'quantity')}
            onWheel={(event) => event.currentTarget.blur()}
            min={1}
            step="1"
            value={line.quantity}
            disabled={busy}
            onChange={(event) => setLine(line.key, { quantity: event.target.value })}
            className={`${controlClass} max-w-16 text-right`}
          />
        ),
    },
    {
      key: 'rate',
      title: 'Price, GST in',
      numeric: true,
      render: (line) => (
        <input
          type="number"
          aria-label={numberLabel(line, 'rate')}
          onWheel={(event) => event.currentTarget.blur()}
          min={0}
          step="0.01"
          value={line.unitPrice}
          disabled={busy}
          onChange={(event) => setLine(line.key, { unitPrice: event.target.value })}
          className={`${controlClass} max-w-28 text-right`}
        />
      ),
    },
    {
      key: 'discount',
      title: 'Off',
      numeric: true,
      render: (line) =>
        mayDiscount ? (
          <input
            aria-label={numberLabel(line, 'discount')}
            placeholder="₹ or %"
            value={line.discount}
            disabled={busy}
            onChange={(event) => setLine(line.key, { discount: event.target.value })}
            className={`${controlClass} font-mono-nums max-w-24 text-right`}
          />
        ) : (
          <span className="text-default">—</span>
        ),
    },
    ...(isBillOfSupply
      ? []
      : [
          {
            key: 'gst',
            title: 'GST %',
            numeric: true,
            render: (line: CartLine) => {
              const figure = lineFigure(line);

              return line.catalogueRate !== null ? (
                <span title="From its design or category">
                  {line.catalogueRate}%{figure ? ` · ${formatAmount(figure.tax)}` : ''}
                </span>
              ) : (
                <div className="flex flex-col items-end gap-0.5">
                  <input
                    type="number"
                    aria-label={numberLabel(line, 'GST rate')}
                    onWheel={(event) => event.currentTarget.blur()}
                    min={0}
                    max={100}
                    step="0.01"
                    placeholder="%"
                    value={line.ratePct}
                    disabled={busy}
                    onChange={(event) => setLine(line.key, { ratePct: event.target.value })}
                    className={`${controlClass} max-w-20 text-right`}
                  />
                  {figure && (
                    <span className="text-default text-[length:var(--scale-list-meta)]">
                      {formatAmount(figure.tax)}
                    </span>
                  )}
                </div>
              );
            },
          } satisfies DocumentColumn<CartLine>,
        ]),
    {
      key: 'total',
      title: 'Amount',
      numeric: true,
      render: (line) => {
        const figure = lineFigure(line);

        return figure ? (
          <div>
            <p className="mb-0 font-semibold text-gray-900">{formatAmount(figure.lineTotal)}</p>
            {figure.discountGiven > 0 && (
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                − {formatAmount(figure.discountGiven)}
              </p>
            )}
          </div>
        ) : (
          <span className="text-default">—</span>
        );
      },
    },
    {
      key: 'remove',
      title: '',
      render: (line) => (
        <IconAction
          icon="icon icon-trash-2"
          label={`Remove line ${lines.indexOf(line) + 1}`}
          onClick={() => setLines((was) => was.filter((l) => l.key !== line.key))}
        />
      ),
    },
  ];

  const customerName = customer?.primary ?? null;
  const settledUp = total !== null && left === 0;

  return (
    <>
      <DocumentHeading
        trail="Sales"
        current="New"
        title="New sale"
        subtitle="Scan a tag or type a name. Every price includes GST."
        onBack={onClose}
        actions={
          <Button icon="icon icon-arrow-left" shortcut="back" disabled={busy} onClick={onClose}>
            Back
          </Button>
        }
      />

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-8">
          <Card>
            <SectionTitle icon="icon icon-scan-line" hue="teal">
              What is being sold
            </SectionTitle>
            <div className="relative">
              <div className="border-border-color focus-within:border-primary flex items-stretch overflow-hidden rounded-md border bg-white">
                <select
                  aria-label="Design"
                  value={design ?? ''}
                  disabled={busy}
                  onChange={(event) => {
                    setDesign(event.target.value || null);
                    setTyped('');
                    setActive(0);
                    setOpen(event.target.value !== '');
                    searchBox.current?.focus();
                  }}
                  className="border-border-color font-mono-nums bg-light w-32 shrink-0 border-e px-2 text-[length:var(--scale-list-name)] font-semibold text-gray-900 outline-none"
                >
                  <option value="">Any design</option>
                  {designs.map((name) => (
                    <option key={name.code} value={name.code}>
                      {name.code} — {name.name}
                    </option>
                  ))}
                </select>
                <i className="icon icon-search text-default self-center ps-3" aria-hidden="true" />
                <input
                  ref={searchBox}
                  aria-label={design ? `Digits on the ${design} tag` : 'Search garments and items'}
                  inputMode={design ? 'numeric' : 'text'}
                  autoComplete="off"
                  placeholder={
                    design
                      ? 'Numbers on its tag — 36, 3601'
                      : 'Scan a tag, or type a code or a name'
                  }
                  value={typed}
                  disabled={busy || scanning}
                  {...{ [ENTER_OWNER_ATTRIBUTE]: '' }}
                  onChange={(event) => {
                    setTyped(design ? event.target.value.replace(/\D/g, '') : event.target.value);
                    setActive(0);
                    setOpen(true);
                    setNote(null);
                  }}
                  onFocus={() => setOpen(true)}
                  onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                  onKeyDown={onSearchKey}
                  className="min-w-0 flex-1 px-3 py-2.5 text-[length:var(--scale-list-name)] text-gray-900 outline-none"
                />
              </div>
              <p className="text-default mt-1.5 mb-0 text-[length:var(--scale-list-meta)]">
                Pick the design's short name, then the numbers on its tag. Any design takes a scan,
                a whole code or a name.
              </p>

              {showing && (
                <div
                  className="absolute inset-x-0 top-12 z-[9999] max-h-96 overflow-y-auto rounded-md border shadow-lg"
                  style={{
                    background: 'var(--overlay-surface)',
                    borderColor: 'var(--overlay-border)',
                  }}
                >
                  {choices.length === 0 ? (
                    <p className="text-default m-0 px-4 py-3 text-[length:var(--scale-list-meta)]">
                      {garments.isFetching
                        ? 'Looking…'
                        : `No ${design} garment starts with ${typed || 'that'}.`}
                    </p>
                  ) : (
                    <ul className="m-0 list-none ps-0">
                      {choices.map((choice, index) => (
                        <ChoiceRow
                          key={choiceKey(choice)}
                          choice={choice}
                          active={index === active}
                          onHover={() => setActive(index)}
                          onPick={() => pickable(choice) && pick(choice)}
                        />
                      ))}
                    </ul>
                  )}
                  <p className="border-border-color text-default m-0 border-t px-4 py-2 text-[11px]">
                    ↑ ↓ move · Enter adds a line · Esc closes
                  </p>
                </div>
              )}
            </div>

            {note && (
              <p className="mt-2 mb-0 text-[length:var(--scale-list-meta)] text-[color:var(--color-warning)]">
                {note}
              </p>
            )}
            {scanError !== null && (
              <div className="mt-2">
                <ApiErrorAlert error={scanError} />
              </div>
            )}

            <div className="mt-4">
              <DocumentLines<CartLine>
                columns={columns}
                rows={lines}
                rowKey={(line) => line.key}
                empty="Find a garment or an item above — each one becomes a line here."
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => setLines((was) => [...was, otherLine(freshKey())])}
                className="border-border-color hover:bg-light inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-dashed bg-white px-3 text-[length:var(--scale-list-name)] text-gray-900"
              >
                <i className="icon icon-plus" aria-hidden="true" />
                Something without a tag — a stole, a mojri
              </button>
              {lines.length > 0 && mayDiscount && (
                <span className="text-default text-[length:var(--scale-list-meta)]">
                  Off: ₹ off the line, or type 5% · your limit is {allowance.data?.maxPct ?? 0}% of
                  the bill
                </span>
              )}
            </div>
            <p className="text-default mt-3 mb-0 text-[length:var(--scale-list-meta)]">
              A garment leaves the rental stock once the bill is saved. It must have nothing booked
              on it from today on.
            </p>

            <div className="border-border-color mt-5 border-t pt-4">
              <Field label="Notes for the bill">
                <textarea
                  rows={3}
                  placeholder="Alteration promised, delivery — anything to print on the bill"
                  value={notes}
                  disabled={busy}
                  onChange={(event) => setNotes(event.target.value)}
                  className={textAreaClass}
                />
              </Field>
            </div>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-4 xl:col-span-4">
          <Card>
            <SectionTitle>Customer</SectionTitle>
            <SearchPicker
              value={customer}
              options={(customers.data?.items ?? []).map((option) => ({
                id: String(option.id),
                primary: option.name,
                secondary: option.mobile,
              }))}
              total={customers.data?.total ?? 0}
              loading={customers.isLoading}
              disabled={busy}
              placeholder="Walk-in — search to bill someone"
              onSearch={setCustomerSearch}
              onSelect={setCustomer}
            />
            <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
              Leave it as walk-in for a sale paid in full that nobody needs on the bill.
            </p>
          </Card>

          <Card>
            <SectionTitle>The bill</SectionTitle>
            <div className="flex flex-col gap-2 text-[length:var(--scale-font-size)]">
              {mayDiscount && (
                <label className="flex items-center justify-between gap-3">
                  <span className="text-default">Off the whole bill</span>
                  <input
                    aria-label="Off the whole bill"
                    placeholder="₹ or %"
                    value={billDiscount}
                    disabled={busy}
                    onChange={(event) => setBillDiscount(event.target.value)}
                    className={`${controlClass} font-mono-nums max-w-28 text-right`}
                  />
                </label>
              )}
              {figures && (
                <>
                  {figures.discountAmount > 0 && (
                    <BillRow label="Saved" value={`− ${rupees(figures.discountAmount)}`} />
                  )}
                  <BillRow
                    label={`${lines.length} item${lines.length === 1 ? '' : 's'}, before tax`}
                    value={rupees(figures.taxableValue)}
                  />
                  {!figures.isBillOfSupply && <BillRow label="GST" value={rupees(figures.tax)} />}
                  {Math.abs(figures.roundOff) >= 0.005 && (
                    <BillRow label="Round off" value={rupees(figures.roundOff)} />
                  )}
                </>
              )}
              <div className="border-border-color flex items-baseline justify-between border-t pt-2">
                <span className="font-bold text-gray-900">Total</span>
                <span className="text-primary font-mono-nums font-serif text-3xl font-semibold">
                  {total === null ? '—' : rupees(total)}
                </span>
              </div>
              <span className="text-default text-[length:var(--scale-list-meta)]">
                {isBillOfSupply ? 'Bill of supply — no GST' : 'Tax invoice'} · worked out by the
                server as it will be saved
              </span>
              {preview.isError && <ApiErrorAlert error={preview.error} />}
            </div>
          </Card>

          <Card>
            <SectionTitle
              trailing={
                total !== null && !settledUp ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setParts((current) => fillOne(current, total))}
                    className="text-primary cursor-pointer text-[length:var(--scale-list-meta)] font-semibold hover:underline"
                  >
                    Paid in full
                  </button>
                ) : undefined
              }
            >
              How they are paying
            </SectionTitle>
            <SplitBoxes parts={parts} onChange={setParts} direction="into" busy={busy} />
            <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
              Take less, or nothing, and the rest is left owed on the bill.
            </p>

            {settledUp && lines.length > 0 && (
              <p className="bg-success-transparent mt-3 mb-0 flex items-center gap-2 rounded-md px-3 py-2.5 font-semibold text-[color:var(--color-success)]">
                <i className="icon icon-check" aria-hidden="true" />
                Paid in full — nothing left owed
              </p>
            )}
            {left !== null && left > 0 && (
              <p
                className={`mt-3 mb-0 text-[length:var(--scale-list-meta)] ${
                  customerName ? 'text-default' : 'text-[color:var(--color-warning)]'
                }`}
              >
                {customerName
                  ? `${rupees(left)} left owed on ${customerName}'s account.`
                  : `${rupees(left)} not paid — pick the customer to leave it on their account.`}
              </p>
            )}

            {(problems.length > 0 || paying.length > 0) && lines.length > 0 && (
              <div className="mt-3">
                <Notice tone="warning" title="Not ready yet">
                  <ul className="mt-1 flex list-disc flex-col gap-0.5 ps-4">
                    {[...problems, ...paying].slice(0, 6).map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                </Notice>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                disabled={busy || !ready}
                onClick={() => void save.mutateAsync().catch(() => undefined)}
                className="h-12 justify-center text-[15px]"
              >
                {busy ? 'Saving…' : sellWords(total, paid)}
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={onClose}
                className="justify-center"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

/** One figure on the bill: what it is on the left, the server's amount on the right. */
function BillRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-default">{label}</span>
      <span className="font-mono-nums font-semibold text-gray-900">{value}</span>
    </div>
  );
}

/** A garment's picture, or the outline where it has none. */
export function Thumb({ url, size = 'size-10' }: { url: string | null; size?: string }) {
  return url ? (
    <img src={url} alt="" className={`${size} shrink-0 rounded object-cover`} />
  ) : (
    <span
      className={`${size} bg-light flex shrink-0 items-center justify-center rounded`}
      aria-hidden="true"
    >
      <i className="icon icon-shirt text-default" />
    </span>
  );
}

function ChoiceRow({
  choice,
  active,
  onHover,
  onPick,
}: {
  choice: Choice;
  active: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const row = `flex list-none cursor-pointer items-center gap-3 px-4 py-2 ${active ? 'bg-light' : ''}`;
  const choose = {
    onMouseEnter: onHover,
    // Before the box's blur closes the list.
    onMouseDown: (event: React.MouseEvent) => {
      event.preventDefault();
      onPick();
    },
  };

  if (choice.kind === 'garment') {
    const { garment } = choice;
    const why = garmentNote(garment);

    return (
      <li className={`${row} ${why ? 'opacity-70' : ''}`} {...choose}>
        <Thumb url={garment.photoUrl} size="size-12" />
        <div className="min-w-0 flex-1">
          <p className="mb-0 truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {garment.itemName || garment.description}{' '}
            <span className="font-mono-nums text-default font-normal">
              ({withReference(garment.serialCode, garment.referenceNumber)})
            </span>
          </p>
          <p
            className={`mb-0 truncate text-[length:var(--scale-list-meta)] ${
              why ? 'text-[color:var(--color-warning)]' : 'text-default'
            }`}
          >
            {why ?? 'Free to sell'}
          </p>
        </div>
        <span className="font-mono-nums text-end text-[length:var(--scale-list-name)] font-semibold text-gray-900">
          {garment.salePrice === null ? 'Type a price' : rupees(garment.salePrice)}
        </span>
      </li>
    );
  }

  if (choice.kind === 'item') {
    const { item } = choice;

    return (
      <li className={row} {...choose}>
        <span
          className="bg-light flex size-12 shrink-0 items-center justify-center rounded"
          aria-hidden="true"
        >
          <i className="icon icon-receipt text-default" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="mb-0 truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {item.description} <span className="text-default font-normal">(item)</span>
          </p>
          <p className="text-default mb-0 truncate text-[length:var(--scale-list-meta)]">
            Sold before{item.hsnCode ? ` · HSN ${item.hsnCode}` : ''}
            {item.ratePct !== null ? ` · ${item.ratePct}% GST` : ''}
          </p>
        </div>
        <span className="font-mono-nums text-end text-[length:var(--scale-list-name)] font-semibold text-gray-900">
          {rupees(item.unitPrice)}
        </span>
      </li>
    );
  }

  return (
    <li className={row} {...choose}>
      <span
        className="bg-primary-transparent flex size-12 shrink-0 items-center justify-center rounded"
        aria-hidden="true"
      >
        <i className="icon icon-plus text-primary" />
      </span>
      <p className="mb-0 text-[length:var(--scale-list-name)] text-gray-900">
        Add <strong>{choice.name}</strong> as an item — you type its price and GST
      </p>
    </li>
  );
}
